import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchCognitoryActivityTraceDetail,
  fetchCognitoryActivityTraceList,
} from "../api/cognitoryQuestionAnswerTraceOwnerReadClient";
import type {
  CognitoryActivityDetailView,
  CognitoryActivitySummaryView,
} from "../api/cognitoryQuestionAnswerTraceOwnerReadRebuild";
import CognitoryAnswerMarkdown from "../presentation/CognitoryAnswerMarkdown";
import {
  appendActivitySummaries,
  canStartActivityLoadMore,
  shouldApplyActivityFetchResult,
} from "../presentation/cognitoryActivityListState";
import {
  COGNITORY_ACTIVITY_DETAIL_FORBIDDEN_COPY,
  COGNITORY_ACTIVITY_INVALID_REQUEST_COPY,
  COGNITORY_ACTIVITY_LIST_FORBIDDEN_COPY,
  COGNITORY_ACTIVITY_SESSION_EXPIRED_COPY,
  formatActivityAttentionLabel,
  formatActivityCitationSummary,
  formatActivityDurationMs,
  formatActivitySupportReference,
} from "../presentation/cognitoryActivityPresentation";
import {
  MAIN_PAGE_BODY_STYLE,
  MAIN_PAGE_COLORS,
  MAIN_PAGE_QUIET_BUTTON_STYLE,
  MAIN_PAGE_RECORD_CARD_STYLE,
  MAIN_PAGE_SECTION_LABEL_STYLE,
} from "../theme/mainPageVisual";
import {
  cognitoryActivityEvaluationListRowFrameStyle,
  cognitoryActivityEvaluationStatusDotColor,
  formatCognitoryActivityEvaluationStatusLabel,
} from "../presentation/cognitoryActivityEvaluationStatus";
import CognitoryQuestionAnswerEvaluationActivitySettings from "./CognitoryQuestionAnswerEvaluationActivitySettings";

type ListPhase =
  | "loading"
  | "empty"
  | "ready"
  | "forbidden"
  | "unauthenticated"
  | "invalid_request"
  | "temporary_failure"
  | "malformed_success";

type DetailPhase =
  | "idle"
  | "loading"
  | "ready"
  | "forbidden"
  | "unauthenticated"
  | "invalid_request"
  | "temporary_failure"
  | "malformed_success";

function ActivitySupportReference(input: { supportReference: string | null }) {
  const formatted = formatActivitySupportReference(input.supportReference);
  if (!formatted) {
    return null;
  }
  return (
    <p
      data-testid="cognitory-activity-support-reference"
      style={{
        margin: "0.75rem 0 0",
        fontSize: "0.82rem",
        color: MAIN_PAGE_COLORS.quietInk,
      }}
    >
      {formatted}
    </p>
  );
}

const {
  border: _activityRowQuietBorder,
  background: _activityRowQuietBackground,
  ...MAIN_PAGE_ACTIVITY_ROW_QUIET_STYLE
} = MAIN_PAGE_QUIET_BUTTON_STYLE;

function ActivitySummaryRow(input: {
  item: CognitoryActivitySummaryView;
  onOpen: (traceRequestId: string) => void;
}) {
  const { item } = input;
  return (
    <li>
      <button
        type="button"
        data-testid="cognitory-activity-row"
        data-trace-request-id={item.traceRequestId}
        style={{
          ...MAIN_PAGE_ACTIVITY_ROW_QUIET_STYLE,
          borderRadius: MAIN_PAGE_RECORD_CARD_STYLE.borderRadius,
          width: "100%",
          marginTop: 0,
          textAlign: "left",
          padding: "0.75rem 0.85rem",
          background: "#ffffff",
          ...cognitoryActivityEvaluationListRowFrameStyle({
            status: item.evaluationStatus,
            panelBorderColor: MAIN_PAGE_COLORS.panelBorder,
          }),
        }}
        onClick={() => input.onOpen(item.traceRequestId)}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "0.75rem",
            marginBottom: "0.35rem",
          }}
        >
          <span style={{ fontWeight: 500, color: MAIN_PAGE_COLORS.inkStrong }}>
            {formatActivityAttentionLabel(item.attentionClass)}
            {item.needsAttention ? (
              <span
                data-testid="cognitory-activity-needs-review"
                style={{
                  marginLeft: "0.45rem",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: MAIN_PAGE_COLORS.inkStrong,
                }}
              >
                Needs review
              </span>
            ) : null}
          </span>
          <time
            dateTime={item.capturedAt}
            style={{ fontSize: "0.82rem", color: MAIN_PAGE_COLORS.quietInk }}
          >
            {new Date(item.capturedAt).toLocaleString()}
          </time>
        </div>
        <p
          style={{
            margin: "0 0 0.35rem",
            color: MAIN_PAGE_COLORS.inkStrong,
            lineHeight: 1.45,
          }}
        >
          {item.question}
        </p>
        <p
          style={{
            margin: "0 0 0.35rem",
            fontSize: "0.82rem",
            color: MAIN_PAGE_COLORS.quietInk,
          }}
        >
          {item.authorLabel} · {formatActivityDurationMs(item.durationMs)}
        </p>
        <p
          data-testid="cognitory-activity-evaluation-status"
          style={{
            margin: 0,
            fontSize: "0.82rem",
            color: MAIN_PAGE_COLORS.quietInk,
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: "0.5rem",
              height: "0.5rem",
              borderRadius: "999px",
              background: cognitoryActivityEvaluationStatusDotColor(
                item.evaluationStatus,
              ),
              flexShrink: 0,
            }}
          />
          {formatCognitoryActivityEvaluationStatusLabel(item.evaluationStatus)}
        </p>
      </button>
    </li>
  );
}

function ActivityDetailBody(input: { trace: CognitoryActivityDetailView }) {
  const { trace } = input;
  const response = trace.response;

  return (
    <article data-testid="cognitory-activity-detail-body">
      <p style={{ ...MAIN_PAGE_SECTION_LABEL_STYLE, marginBottom: "0.35rem" }}>
        QUESTION
      </p>
      <p
        data-testid="cognitory-activity-detail-question"
        style={{ margin: "0 0 1rem", lineHeight: 1.5 }}
      >
        {trace.question}
      </p>
      <p style={{ ...MAIN_PAGE_SECTION_LABEL_STYLE, marginBottom: "0.35rem" }}>
        OUTCOME
      </p>
      <p style={{ margin: "0 0 0.75rem" }}>
        {formatActivityAttentionLabel(trace.attentionClass)} ·{" "}
        {trace.authorLabel} · {formatActivityDurationMs(trace.durationMs)}
      </p>
      <time
        dateTime={trace.capturedAt}
        style={{
          display: "block",
          marginBottom: "0.75rem",
          fontSize: "0.9rem",
        }}
      >
        Captured {new Date(trace.capturedAt).toLocaleString()}
      </time>
      <p
        data-testid="cognitory-activity-detail-expires"
        style={{
          margin: "0 0 1rem",
          fontSize: "0.78rem",
          color: MAIN_PAGE_COLORS.quietInk,
        }}
      >
        Retention expires {new Date(trace.expiresAt).toLocaleString()}
      </p>
      {response.kind === "answer_rendered" ? (
        <div data-testid="cognitory-activity-detail-answer">
          <CognitoryAnswerMarkdown text={response.answerText} />
          <p
            style={{
              marginTop: "0.75rem",
              fontSize: "0.85rem",
              color: MAIN_PAGE_COLORS.quietInk,
            }}
          >
            {formatActivityCitationSummary({
              renderedCitationCount: response.renderedCitationCount,
              citationLabels: response.citations.map(
                citation => citation.citationLabel,
              ),
            })}
          </p>
        </div>
      ) : null}
      {response.kind === "refusal_rendered" ? (
        <div data-testid="cognitory-activity-detail-refusal">
          <CognitoryAnswerMarkdown text={response.refusalText} />
          <p
            style={{
              marginTop: "0.75rem",
              fontSize: "0.85rem",
              color: MAIN_PAGE_COLORS.quietInk,
            }}
          >
            {formatActivityCitationSummary({
              renderedCitationCount: response.renderedCitationCount,
              citationLabels: response.citations.map(
                citation => citation.citationLabel,
              ),
            })}
          </p>
        </div>
      ) : null}
      {response.kind === "error_response" ? (
        <p data-testid="cognitory-activity-detail-error-decision">
          Response decision: {response.decision}
        </p>
      ) : null}
      {trace.diagnostics ? (
        <section
          data-testid="cognitory-activity-detail-diagnostics"
          style={{ marginTop: "1.25rem" }}
        >
          <p
            style={{ ...MAIN_PAGE_SECTION_LABEL_STYLE, marginBottom: "0.5rem" }}
          >
            DIAGNOSTICS
          </p>
          <dl
            style={{
              margin: 0,
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
              gap: "0.35rem 0.75rem",
              fontSize: "0.85rem",
            }}
          >
            {(
              [
                ["embeddingCallCount", trace.diagnostics.embeddingCallCount],
                ["pineconeQueryCount", trace.diagnostics.pineconeQueryCount],
                ["modelCallCount", trace.diagnostics.modelCallCount],
                ["returnedMatchCount", trace.diagnostics.returnedMatchCount],
                [
                  "acceptedEvidenceItemCount",
                  trace.diagnostics.acceptedEvidenceItemCount,
                ],
                ["retrievalDecision", trace.diagnostics.retrievalDecision],
                ["packetDecision", trace.diagnostics.packetDecision],
                [
                  "claimVerificationDecision",
                  trace.diagnostics.claimVerificationDecision,
                ],
                ["rendererDecision", trace.diagnostics.rendererDecision],
                ["refusalStage", trace.diagnostics.refusalStage],
                [
                  "recommendedRefusalReason",
                  trace.diagnostics.recommendedRefusalReason,
                ],
                ["internalFailure", trace.diagnostics.internalFailure],
                ["detail", trace.diagnostics.detail],
              ] as const
            ).map(([label, value]) =>
              value === undefined ? null : (
                <div key={label} style={{ display: "contents" }}>
                  <dt style={{ color: MAIN_PAGE_COLORS.quietInk }}>{label}</dt>
                  <dd style={{ margin: 0 }}>{String(value)}</dd>
                </div>
              ),
            )}
          </dl>
        </section>
      ) : null}
    </article>
  );
}

export default function CognitoryActivityShell(input: {
  cognitoryId: string;
  active: boolean;
  evaluationFeatureAvailable: boolean;
  evaluationCollectionEnabled: boolean;
  canManageQuestionAnswerEvaluation: boolean;
  onEvaluationCollectionEnabledChange?: (enabled: boolean) => void;
}) {
  const [listPhase, setListPhase] = useState<ListPhase>("loading");
  const [items, setItems] = useState<CognitoryActivitySummaryView[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [listSupportReference, setListSupportReference] = useState<
    string | null
  >(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [selectedTraceRequestId, setSelectedTraceRequestId] = useState<
    string | null
  >(null);
  const [detailPhase, setDetailPhase] = useState<DetailPhase>("idle");
  const [detailTrace, setDetailTrace] =
    useState<CognitoryActivityDetailView | null>(null);
  const [detailSupportReference, setDetailSupportReference] = useState<
    string | null
  >(null);

  const listGenerationRef = useRef(0);
  const detailGenerationRef = useRef(0);
  const loadMoreInFlightRef = useRef(false);
  const pendingDetailTraceRequestIdRef = useRef<string | null>(null);

  const resetListState = useCallback(() => {
    listGenerationRef.current += 1;
    detailGenerationRef.current += 1;
    loadMoreInFlightRef.current = false;
    setListPhase("loading");
    setItems([]);
    setNextCursor(null);
    setListSupportReference(null);
    setLoadingMore(false);
    setLoadMoreFailed(false);
    setSelectedTraceRequestId(null);
    setDetailPhase("idle");
    setDetailTrace(null);
    setDetailSupportReference(null);
  }, []);

  const loadList = useCallback(
    async (mode: "initial" | "append", cursor?: string | null) => {
      const generation = listGenerationRef.current;
      const cognitoryId = input.cognitoryId;
      if (mode === "append") {
        if (
          !canStartActivityLoadMore({
            loadingMore: loadMoreInFlightRef.current,
            nextCursor: cursor ?? null,
          })
        ) {
          return;
        }
        loadMoreInFlightRef.current = true;
        setLoadingMore(true);
        setLoadMoreFailed(false);
      }

      const listRequest =
        mode === "append" && typeof cursor === "string" && cursor.length > 0
          ? { cognitoryId, cursor }
          : { cognitoryId };
      const result = await fetchCognitoryActivityTraceList(listRequest);

      if (
        !shouldApplyActivityFetchResult({
          requestGeneration: generation,
          activeGeneration: listGenerationRef.current,
          requestCognitoryId: cognitoryId,
          activeCognitoryId: input.cognitoryId,
        })
      ) {
        if (mode === "append") {
          loadMoreInFlightRef.current = false;
          setLoadingMore(false);
        }
        return;
      }

      if (mode === "append") {
        loadMoreInFlightRef.current = false;
        setLoadingMore(false);
      }

      if (result.kind === "success") {
        setListSupportReference(result.supportReference);
        setNextCursor(result.nextCursor);
        if (mode === "append") {
          setLoadMoreFailed(false);
          setItems(current =>
            appendActivitySummaries({
              current,
              incoming: result.items,
            }),
          );
        } else {
          setItems(result.items);
          setListPhase(result.items.length === 0 ? "empty" : "ready");
        }
        return;
      }

      if (mode === "append") {
        setLoadMoreFailed(true);
        if (result.supportReference) {
          setListSupportReference(result.supportReference);
        }
        return;
      }

      setListSupportReference(result.supportReference);
      setItems([]);
      setNextCursor(null);
      setListPhase(result.kind);
    },
    [input.cognitoryId],
  );

  const loadDetail = useCallback(
    async (traceRequestId: string) => {
      detailGenerationRef.current += 1;
      const generation = detailGenerationRef.current;
      const cognitoryId = input.cognitoryId;
      setSelectedTraceRequestId(traceRequestId);
      pendingDetailTraceRequestIdRef.current = traceRequestId;
      setDetailPhase("loading");
      setDetailTrace(null);
      setDetailSupportReference(null);

      const result = await fetchCognitoryActivityTraceDetail({
        cognitoryId,
        traceRequestId,
      });

      if (
        !shouldApplyActivityFetchResult({
          requestGeneration: generation,
          activeGeneration: detailGenerationRef.current,
          requestCognitoryId: cognitoryId,
          activeCognitoryId: input.cognitoryId,
        })
      ) {
        return;
      }
      if (pendingDetailTraceRequestIdRef.current !== traceRequestId) {
        return;
      }

      setDetailSupportReference(result.supportReference);
      if (result.kind === "success") {
        if (result.trace.traceRequestId !== traceRequestId) {
          setDetailPhase("malformed_success");
          return;
        }
        setDetailTrace(result.trace);
        setDetailPhase("ready");
        return;
      }
      setDetailTrace(null);
      setDetailPhase(result.kind);
    },
    [input.cognitoryId],
  );

  useEffect(() => {
    resetListState();
  }, [input.cognitoryId, resetListState]);

  useEffect(() => {
    if (!input.active) {
      listGenerationRef.current += 1;
      detailGenerationRef.current += 1;
      return;
    }
    void loadList("initial");
  }, [input.active, input.cognitoryId, loadList]);

  const handleBackToList = () => {
    detailGenerationRef.current += 1;
    setSelectedTraceRequestId(null);
    setDetailPhase("idle");
    setDetailTrace(null);
    setDetailSupportReference(null);
  };

  const showDetail = selectedTraceRequestId !== null;

  return (
    <div
      data-testid="cognitory-activity-shell"
      style={{ ...MAIN_PAGE_BODY_STYLE, marginTop: "0.25rem" }}
    >
      {showDetail ? (
        <div data-testid="cognitory-activity-detail">
          <button
            type="button"
            data-testid="cognitory-activity-back"
            style={{ ...MAIN_PAGE_QUIET_BUTTON_STYLE, marginTop: 0 }}
            onClick={handleBackToList}
          >
            Back to activity
          </button>
          {detailPhase === "loading" ? (
            <p data-testid="cognitory-activity-detail-loading">Loading…</p>
          ) : null}
          {detailPhase === "forbidden" ? (
            <p data-testid="cognitory-activity-detail-forbidden">
              {COGNITORY_ACTIVITY_DETAIL_FORBIDDEN_COPY}
            </p>
          ) : null}
          {detailPhase === "unauthenticated" ? (
            <p data-testid="cognitory-activity-detail-unauthenticated">
              {COGNITORY_ACTIVITY_SESSION_EXPIRED_COPY}
            </p>
          ) : null}
          {detailPhase === "invalid_request" ? (
            <p data-testid="cognitory-activity-detail-invalid-request">
              {COGNITORY_ACTIVITY_INVALID_REQUEST_COPY}
            </p>
          ) : null}
          {detailPhase === "temporary_failure" ? (
            <p data-testid="cognitory-activity-detail-temporary-failure">
              Unable to load this activity item right now.
            </p>
          ) : null}
          {detailPhase === "malformed_success" ? (
            <p data-testid="cognitory-activity-detail-malformed">
              Activity data could not be displayed safely.
            </p>
          ) : null}
          {detailPhase === "ready" && detailTrace ? (
            <ActivityDetailBody trace={detailTrace} />
          ) : null}
          <ActivitySupportReference supportReference={detailSupportReference} />
        </div>
      ) : (
        <div data-testid="cognitory-activity-list">
          <h2
            style={{
              ...MAIN_PAGE_SECTION_LABEL_STYLE,
              marginBottom: "0.75rem",
              textAlign: "center",
            }}
          >
            Recent responses
          </h2>
          {input.canManageQuestionAnswerEvaluation ? (
            <CognitoryQuestionAnswerEvaluationActivitySettings
              cognitoryId={input.cognitoryId}
              featureAvailable={input.evaluationFeatureAvailable}
              {...(input.onEvaluationCollectionEnabledChange
                ? {
                    onCollectionEnabledChange:
                      input.onEvaluationCollectionEnabledChange,
                  }
                : {})}
            />
          ) : null}
          {listPhase === "loading" ? (
            <p data-testid="cognitory-activity-list-loading">
              Loading activity…
            </p>
          ) : null}
          {listPhase === "empty" ? (
            <p data-testid="cognitory-activity-list-empty">
              No activity recorded for this Cognitory yet.
            </p>
          ) : null}
          {listPhase === "forbidden" ? (
            <p data-testid="cognitory-activity-list-forbidden">
              {COGNITORY_ACTIVITY_LIST_FORBIDDEN_COPY}
            </p>
          ) : null}
          {listPhase === "unauthenticated" ? (
            <p data-testid="cognitory-activity-list-unauthenticated">
              {COGNITORY_ACTIVITY_SESSION_EXPIRED_COPY}
            </p>
          ) : null}
          {listPhase === "invalid_request" ? (
            <p data-testid="cognitory-activity-list-invalid-request">
              {COGNITORY_ACTIVITY_INVALID_REQUEST_COPY}
            </p>
          ) : null}
          {listPhase === "temporary_failure" ? (
            <div data-testid="cognitory-activity-list-temporary-failure">
              <p>Unable to load activity right now.</p>
              <button
                type="button"
                data-testid="cognitory-activity-list-retry"
                style={{ ...MAIN_PAGE_QUIET_BUTTON_STYLE, marginTop: "0.5rem" }}
                onClick={() => {
                  setListPhase("loading");
                  void loadList("initial");
                }}
              >
                Retry
              </button>
            </div>
          ) : null}
          {listPhase === "malformed_success" ? (
            <p data-testid="cognitory-activity-list-malformed">
              Activity data could not be displayed safely.
            </p>
          ) : null}
          {listPhase === "ready" ? (
            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
                display: "grid",
                gap: "0.55rem",
              }}
            >
              {items.map(item => (
                <ActivitySummaryRow
                  key={item.traceRequestId}
                  item={item}
                  onOpen={traceRequestId => void loadDetail(traceRequestId)}
                />
              ))}
            </ul>
          ) : null}
          {listPhase === "ready" && nextCursor ? (
            <div style={{ marginTop: "0.85rem" }}>
              {loadMoreFailed ? (
                <p
                  data-testid="cognitory-activity-load-more-failure"
                  style={{
                    margin: "0 0 0.5rem",
                    fontSize: "0.85rem",
                    color: MAIN_PAGE_COLORS.quietInk,
                  }}
                >
                  Unable to load more activity right now.
                </p>
              ) : null}
              <button
                type="button"
                data-testid="cognitory-activity-load-more"
                disabled={loadingMore}
                style={{ ...MAIN_PAGE_QUIET_BUTTON_STYLE, marginTop: 0 }}
                onClick={() => void loadList("append", nextCursor)}
              >
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          ) : null}
          <ActivitySupportReference supportReference={listSupportReference} />
        </div>
      )}
    </div>
  );
}
